import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Save, Upload as UploadIcon } from 'lucide-react';
import { NotificationManager } from 'react-notifications';

import FormStepIndicator from './FormStepIndicator';
import Step1BasicInfo from './FormSteps/Step1BasicInfo';
import Step2PropertyDetails from './FormSteps/Step2PropertyDetails';
import Step3PricingTerms from './FormSteps/Step3PricingTerms';
import Step4Features from './FormSteps/Step4Features';
import Step5Images from './FormSteps/Step5Images';
import Step6Additional from './FormSteps/Step6Additional';
import Step7Review from './FormSteps/Step7Review';

import landlordPropertyService from '../../services/landlordPropertyService';
import { validatePropertyData, isStepValid } from '../../utils/propertyValidation';
import { FORM_STEPS } from '../../constants/propertyConstants';

/**
 * PropertyForm Component
 * Multi-step wizard for creating and editing property listings
 */
const PropertyForm = () => {
    const navigate = useNavigate();
    const { propertyId } = useParams();
    const isEditMode = !!propertyId;

    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [formData, setFormData] = useState({
        currency: 'KES',
        allowContactViaEmail: true,
        allowContactViaPhone: true,
        allowViewingRequests: true
    });
    const [images, setImages] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(isEditMode);

    // Load existing property data if editing
    useEffect(() => {
        if (isEditMode) {
            loadPropertyData();
        }
    }, [propertyId]);

    const loadPropertyData = async () => {
        try {
            const property = await landlordPropertyService.getPropertyDetails(propertyId);
            setFormData(property);
            // Note: Images are already uploaded, so we don't load them into the image array
            setLoadingData(false);
        } catch (error) {
            console.error('Error loading property:', error);
            NotificationManager.error('Failed to load property data', 'Error');
            navigate('/landlord/properties');
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleNext = () => {
        // Validate current step
        if (!isStepValid(currentStep, formData)) {
            NotificationManager.warning('Please fill all required fields', 'Validation Error');
            return;
        }

        // Mark step as completed
        if (!completedSteps.includes(currentStep)) {
            setCompletedSteps(prev => [...prev, currentStep]);
        }

        // Move to next step
        if (currentStep < FORM_STEPS.length) {
            setCurrentStep(currentStep + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleGoToStep = (step) => {
        setCurrentStep(step);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (publishImmediately = false) => {
        // Final validation
        const validation = validatePropertyData(formData);
        if (!validation.isValid) {
            setErrors(validation.errors);
            NotificationManager.error('Please fix validation errors', 'Validation Error');
            // Go back to first step with errors
            const firstErrorStep = Object.keys(validation.errors)[0];
            if (firstErrorStep) {
                setCurrentStep(1); // Simplified - could map field to step
            }
            return;
        }

        setLoading(true);

        try {
            let propertyIdToUse = propertyId;

            // Drop any "Add Link" rows the landlord never filled in
            const submitData = {
                ...formData,
                otherMediaLinks: (formData.otherMediaLinks || []).filter((link) => link.trim() !== '')
            };

            // Step 1: Create or update property
            if (isEditMode) {
                await landlordPropertyService.updateProperty(propertyId, submitData);
                NotificationManager.success('Property updated successfully', 'Success');
            } else {
                const result = await landlordPropertyService.createProperty(submitData);
                propertyIdToUse = result.propertyId;
                NotificationManager.success('Property created successfully', 'Success');
            }

            // Step 2: Upload images if any
            if (images.length > 0) {
                try {
                    await landlordPropertyService.uploadImages(propertyIdToUse, images);
                    NotificationManager.success(`${images.length} image(s) uploaded`, 'Success');
                } catch (imageError) {
                    console.error('Error uploading images:', imageError);
                    NotificationManager.warning('Property saved but some images failed to upload', 'Warning');
                }
            }

            // Step 3: Publish if requested
            if (publishImmediately) {
                try {
                    await landlordPropertyService.publishProperty(propertyIdToUse);
                    NotificationManager.success('Property published successfully', 'Success');
                } catch (publishError) {
                    console.error('Error publishing property:', publishError);
                    NotificationManager.warning('Property saved but failed to publish', 'Warning');
                }
            }

            // Navigate to property details or dashboard
            navigate(`/landlord/properties/${propertyIdToUse}`);

        } catch (error) {
            console.error('Error saving property:', error);
            NotificationManager.error(error.message || 'Failed to save property', 'Error');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Step1BasicInfo
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                    />
                );
            case 2:
                return (
                    <Step2PropertyDetails
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                    />
                );
            case 3:
                return (
                    <Step3PricingTerms
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                    />
                );
            case 4:
                return (
                    <Step4Features
                        formData={formData}
                        onChange={handleFieldChange}
                    />
                );
            case 5:
                return (
                    <Step5Images
                        images={images}
                        onImagesChange={setImages}
                    />
                );
            case 6:
                return (
                    <Step6Additional
                        formData={formData}
                        onChange={handleFieldChange}
                    />
                );
            case 7:
                return (
                    <Step7Review
                        formData={formData}
                        images={images}
                    />
                );
            default:
                return null;
        }
    };

    if (loadingData) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading property data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/landlord/properties')}
                        className="flex items-center text-soil-600 hover:text-soil-900 mb-4"
                    >
                        <ChevronLeft className="h-5 w-5 mr-1" />
                        Back to Properties
                    </button>
                    <h1 className="font-display text-3xl font-bold text-soil-900">
                        {isEditMode ? 'Edit Property' : 'Create New Property'}
                    </h1>
                    <p className="mt-2 text-soil-600">
                        {isEditMode
                            ? 'Update your property listing details'
                            : 'Fill in the details to list your property'}
                    </p>
                </div>

                {/* Progress Indicator */}
                <FormStepIndicator
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                />

                {/* Form Content */}
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-6">
                    {renderStep()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-4">
                    <button
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                        className={`
                            flex items-center px-4 py-2 rounded-lg font-medium transition-colors
                            ${currentStep === 1
                                ? 'bg-soil-100 text-soil-400 cursor-not-allowed'
                                : 'bg-soil-100 text-soil-600 hover:bg-soil-100/70'
                            }
                        `}
                    >
                        <ChevronLeft className="h-5 w-5 mr-1" />
                        Previous
                    </button>

                    <div className="flex items-center space-x-3">
                        {currentStep === FORM_STEPS.length ? (
                            <>
                                <button
                                    onClick={() => handleSubmit(false)}
                                    disabled={loading}
                                    className="flex items-center px-6 py-2 bg-soil-600 text-white rounded-lg font-medium hover:bg-soil-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Save className="h-5 w-5 mr-2" />
                                    {loading ? 'Saving...' : 'Save as Draft'}
                                </button>
                                <button
                                    onClick={() => handleSubmit(true)}
                                    disabled={loading}
                                    className="flex items-center px-6 py-2 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <UploadIcon className="h-5 w-5 mr-2" />
                                    {loading ? 'Publishing...' : 'Publish Now'}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handleNext}
                                className="flex items-center px-6 py-2 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 transition-colors"
                            >
                                Next
                                <ChevronRight className="h-5 w-5 ml-1" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Quick Navigation */}
                <div className="mt-4 text-center">
                    <p className="text-sm text-soil-600 mb-2">Quick Jump:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                        {FORM_STEPS.map((step) => (
                            <button
                                key={step.id}
                                onClick={() => handleGoToStep(step.id)}
                                className={`
                                    px-3 py-1 text-xs font-medium rounded-full transition-colors
                                    ${currentStep === step.id
                                        ? 'bg-highland-500 text-white'
                                        : completedSteps.includes(step.id)
                                        ? 'bg-highland-50 text-highland-600 hover:bg-highland-100'
                                        : 'bg-soil-100 text-soil-600 hover:bg-soil-100/70'
                                    }
                                `}
                            >
                                {step.name}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyForm;
